import type { ActionStreet, Card, HandRecord, HandRecordAction, HandView, SeatView } from '@tctp/protocol';
import { cardView } from './cardView';

export type Tone = 'plain' | 'bold' | 'green' | 'red' | 'blue' | 'gold';
export interface LogLine { t: number; text: string; tone: Tone; handId?: string }   // `handId` on a hand's closing line: the log offers a replay there

const glyph = (c: Card): string => { const v = cardView(c); return `${v.rank}${v.glyph}`; };
const list = (cards: Card[]): string => `[${cards.map(glyph).join(', ')}]`;

export function describeAction(a: HandRecordAction, who: string, smallBlind: number): LogLine {
  const auto = a.auto ? ' (timed out)' : '';
  switch (a.type) {
    case 'blind': return { t: a.t, text: `${who} posts a ${a.amount === smallBlind ? 'small' : 'big'} blind of ${a.amount}`, tone: 'green' };
    case 'discardLocked': return { t: a.t, text: `${who} locks in a discard${auto}`, tone: 'plain' };
    case 'fold': return { t: a.t, text: `${who} folds${auto}`, tone: 'red' };
    case 'check': return { t: a.t, text: `${who} checks${auto}`, tone: 'plain' };
    case 'call': return { t: a.t, text: `${who} calls ${a.amount}`, tone: 'blue' };
    case 'raise': return { t: a.t, text: `${who} raises to ${a.to}`, tone: 'green' };
    case 'show': return { t: a.t, text: `${who} shows ${a.cards!.map(glyph).join(' ')}`, tone: 'plain' };
  }
}

const CARDS: Record<ActionStreet, number> = { discard: 0, preflop: 0, flop: 3, turn: 4, river: 5 };
const STREETS = [{ name: 'Flop', from: 0, to: 3 }, { name: 'Turn', from: 3, to: 4 }, { name: 'River', from: 4, to: 5 }];

/**
 * The actions in order, with a line for each street as its cards arrived — before the street's first action, or after the last action when the rest ran out —
 * showing the pot as it stood then (everything committed so far, never more than what is actually contested: a bet nobody called comes back and was never in the pot), and run 2's own streets when the board was run twice.
 * Logs from before actions carried a street get no street lines.
 */
function playLines(actions: HandRecordAction[], board: Card[], board2: Card[] | null, nick: (seat: number) => string, smallBlind: number, endT: number, contested = Infinity): LogLine[] {
  const out: LogLine[] = [];
  const streets = actions.every(a => a.street !== undefined);
  let dealt = 0;
  let pot = 0;
  const deal = (upTo: number, t: number): void => {
    const shown = Math.min(pot, contested);   // an all-in nobody could cover is refunded: it never joins the pot the streets are played for
    for (const s of STREETS) if (s.to > dealt && s.to <= upTo) out.push({ t, text: `${s.name}: ${board.slice(s.from, s.to).map(glyph).join(' ')} · pot ${shown}`, tone: 'plain' });
    dealt = Math.max(dealt, upTo);
  };
  for (const a of actions) {
    if (streets) deal(CARDS[a.street], a.t);
    pot += a.amount ?? 0;
    out.push(describeAction(a, nick(a.seat), smallBlind));
  }
  if (streets) deal(board.length, endT);
  if (board2) {
    let shared = 0;   // the leading cards both runs share (the second board repeats them)
    while (shared < board.length && shared < board2.length && board[shared] === board2[shared]) shared++;
    for (const s of STREETS) if (s.to > shared && s.to <= board2.length) out.push({ t: endT, text: `Run 2 ${s.name.toLowerCase()}: ${board2.slice(s.from, s.to).map(glyph).join(' ')}`, tone: 'plain' });
  }
  return out;
}

function recordLines(r: HandRecord): LogLine[] {
  const nick = (seat: number): string => r.players.find(p => p.seat === seat)?.nickname ?? `Seat ${seat + 1}`;
  const out: LogLine[] = [];
  out.push({ t: r.startedAt, text: `-- starting hand #${r.handNumber} (id: ${r.handId}) --`, tone: 'bold' });
  out.push({ t: r.startedAt, text: `Player stacks: ${r.players.map(p => `#${p.seat + 1} ${p.nickname} (${p.startStack})`).join(' | ')}`, tone: 'plain' });
  const contested = r.awards.reduce((n, a) => n + a.amount, 0);   // every chip that was played for, the returned uncalled bet excluded
  out.push(...playLines(r.actions.filter(a => a.type !== 'show'), r.board.map(o => o.card), r.board2?.map(o => o.card) ?? null, nick, r.smallBlind, r.endedAt, contested));   // shows are listed once, from `showdown` / `shows`
  if (r.board2) {
    out.push({ t: r.endedAt, text: `Board #1: ${list(r.board.map(o => o.card))}`, tone: 'plain' });
    out.push({ t: r.endedAt, text: `Board #2: ${list(r.board2.map(o => o.card))}`, tone: 'plain' });
  } else if (r.board.length > 0) out.push({ t: r.endedAt, text: `Board: ${list(r.board.map(o => o.card))}`, tone: 'plain' });
  for (const d of r.discards) out.push({ t: r.endedAt, text: `${nick(d.seat)} discarded ${glyph(d.opening.card)}`, tone: 'plain' });
  for (const s of r.showdown) {
    const second = r.showdown2?.find(x => x.seat === s.seat);
    out.push({ t: r.endedAt, text: `${nick(s.seat)} shows ${list(s.keepers)} — ${second ? `#1 ${s.categoryName}, #2 ${second.categoryName}` : s.categoryName}`, tone: 'plain' });
  }
  for (const s of r.shows) out.push({ t: r.endedAt, text: `${nick(s.seat)} shows ${s.cards.map(glyph).join(' ')}`, tone: 'plain' });
  if (r.refund) out.push({ t: r.endedAt, text: `Uncalled bet of ${r.refund.amount} returned to ${nick(r.refund.seat)}`, tone: 'plain' });
  const won = new Map<number, number>();
  for (const aw of r.awards) for (const sh of aw.shares) won.set(sh.seat, (won.get(sh.seat) ?? 0) + sh.amount);
  for (const [seat, amount] of won) out.push({ t: r.endedAt, text: `${nick(seat)} collected ${amount} from pot${r.board2 ? ' (run twice)' : ''}`, tone: 'gold' });
  out.push({ t: r.endedAt, text: `-- ending hand #${r.handNumber} --`, tone: 'bold', handId: r.handId });
  return out;
}

function currentLines(h: HandView, seats: Array<SeatView | null>, smallBlind: number): LogLine[] {
  const nick = (seat: number): string => seats[seat]?.nickname ?? `Seat ${seat + 1}`;
  const out: LogLine[] = [{ t: h.actions[0]?.t ?? 0, text: `-- hand in progress (id: ${h.handId}) --`, tone: 'bold' }];
  out.push(...playLines(h.actions, h.board, h.board2, nick, smallBlind, h.actions.at(-1)?.t ?? 0, h.results ? h.results.awards.reduce((n, a) => n + a.amount, 0) : Infinity));
  if (h.board.length > 0) out.push({ t: h.actions.at(-1)?.t ?? 0, text: `Board so far: ${list(h.board)}`, tone: 'plain' });
  return out;
}

/** Session log lines, newest first: the hand in progress (if any), then each finished hand, latest first. */
export function logLines(history: HandRecord[], current: HandView | null, seats: Array<SeatView | null>, smallBlind = history.at(-1)?.smallBlind ?? 1): LogLine[] {
  const chronological: LogLine[] = [];
  for (const r of history) chronological.push(...recordLines(r));
  if (current && current.phase !== 'complete') chronological.push(...currentLines(current, seats, smallBlind));
  return chronological.reverse();
}
